import clsx from "clsx"
import { FC, memo } from "react"
import { useTranslation } from "react-i18next"
import { AMOUNT_HINT_ATTRIBUTE } from "@/helpers/money"
import { Container } from "@/ui/Container"
import { CurrencyIcon } from "@/ui/CurrencyIcon"
import { RollingNumber } from "@/ui/RollingNumber"
import { ICurrencyPlaque } from "@/ui/WalletCounters/ui/CurrencyPlaque/types"
import styles from "./CurrencyPlaque.module.scss"

const HINT_ATTRIBUTES = { [AMOUNT_HINT_ATTRIBUTE]: "" }

const CurrencyPlaqueComponent: FC<ICurrencyPlaque> = ({
  counter,
  onToggleHint
}) => {
  const { t } = useTranslation()
  const number = (
    <span className={styles.CurrencyPlaque_reserve}>
      {counter.widthTexts.map((text, index) => (
        <span
          key={`${index}:${text}`}
          className={styles.CurrencyPlaque_sizer}
          aria-hidden="true"
        >
          {text}
        </span>
      ))}
      <span className={styles.CurrencyPlaque_number}>
        <RollingNumber
          characters={counter.rollingCharacters}
          direction={counter.direction}
        />
      </span>
    </span>
  )

  return (
    <Container
      triangleTopPosLeft="80%"
      triangleBottomPosLeft="20%"
      className={clsx(styles.CurrencyPlaque, styles[counter.tone])}
    >
      <CurrencyIcon
        iconUrl={counter.iconUrl}
        alt={t(`currency.${counter.currencyCode}.name`, { ns: "catalog" })}
        size="medium"
      />
      {counter.exactText === null ? (
        <span className={styles.CurrencyPlaque_amount}>{number}</span>
      ) : (
        <>
          <button
            type="button"
            className={styles.CurrencyPlaque_amount}
            {...HINT_ATTRIBUTES}
            aria-expanded={counter.isHintOpen}
            onClick={() => onToggleHint(counter.currencyCode)}
          >
            {number}
          </button>
          <span
            role="tooltip"
            className={clsx(styles.CurrencyPlaque_hint, {
              [styles.open]: counter.isHintOpen
            })}
          >
            {counter.exactText}
          </span>
        </>
      )}
      {counter.hasAddButton && (
        <button type="button" className={styles.CurrencyPlaque_add}>
          <span className={styles.CurrencyPlaque_addLabel}>+</span>
        </button>
      )}
      {counter.changeId > 0 && (
        <span
          key={counter.changeId}
          className={styles.CurrencyPlaque_pulse}
          aria-hidden="true"
        />
      )}
    </Container>
  )
}

export const CurrencyPlaque = memo(CurrencyPlaqueComponent)
